/**
 * Registrar Conduce x Mayor — entrega de motores en consignación al cliente.
 *
 * El cliente se lleva los motores a vender: la casa no le ha vendido nada
 * todavía, así que el conduce NO lleva tipo de comprobante ni plan de cuotas y
 * no genera deuda. Cuando el cliente reporte una venta se marca vendido en el
 * detalle del conduce y de ahí sale la factura (conducedetallexmayor.js).
 *
 * Mismo formulario que facturarxmayor.js sin la parte fiscal, siguiendo el
 * precedente de crearcotizacionxmayor.js: cliente, moneda/tasa y la grilla de
 * chasis con su precio pactado y el mínimo del chasis.
 *
 * Multi-empresa: no se elige la empresa. Al guardar el servidor crea un
 * conduce por cada empresa a la que pertenecen los chasis.
 *
 * El conduce a medio llenar se guarda solo en el navegador (borrador
 * "conduce", ver lib/borradorventa) y se retoma al volver; los chasis se
 * revalidan al restaurar porque no quedan reservados.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { precioEnMoneda, monedaLabel as etiquetaMoneda } from "lib/moneda";
import { fmtMoney, imprimirConduce } from "modules/ventasxmayor/impresionxmayor";
import { useBorradorVenta } from "lib/borradorventa";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";

const filtrarClientes = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.id} ${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const filtrarProductos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Hoy en hora local (toISOString daría mañana en la noche, UTC).
const hoyLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color, extra }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
      {extra}
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Agregue los chasis a entregar
      </Typography>
    </Stack>
  );
}

export default function CrearConduceXmayor({
  embedded = false,
  onExit,
  onSaved,
  restaurarBorrador = false,
} = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [{ usuarioLog }] = useMaterialUIController();

  const [config, setConfig] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [cliente, setCliente] = useState(null);
  const [moneda, setMoneda] = useState("P");
  const [tasa, setTasa] = useState("");
  const [concepto, setConcepto] = useState("");
  // Vacía = el backend la graba con la fecha de hoy.
  const [fecha, setFecha] = useState("");
  const [items, setItems] = useState([]);
  const [prodSel, setProdSel] = useState(null);
  const [validando, setValidando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // Conduces creados en el último guardado cuando salió más de uno (uno por
  // empresa): se listan para imprimirlos uno a uno.
  const [emitidos, setEmitidos] = useState(null);

  const cargarProductos = async () => {
    const { ok, data } = await post("/api/xmayor_productos", {});
    if (ok) setProductos(data.productos || []);
  };

  // Devuelve { conf, clientes } para que el borrador se restaure sobre la
  // configuración ya cargada (ver restaurar).
  const cargar = async () => {
    setCargando(true);
    try {
      const [conf, cli, prod] = await Promise.all([
        post("/api/xmayor_config", {}),
        post("/api/xmayor_clientes", {}),
        post("/api/xmayor_productos", {}),
      ]);
      if (conf.ok) {
        setConfig(conf.data);
        setTasa(String(conf.data.tasaus ?? ""));
      } else {
        showAlert({
          message: conf.data.Error || "No se pudo cargar la configuración",
          color: "error",
        });
      }
      if (cli.ok) setClientes(cli.data.clientes || []);
      if (prod.ok) setProductos(prod.data.productos || []);
      return {
        conf: conf.ok ? conf.data : null,
        clientes: cli.ok ? cli.data.clientes || [] : [],
        productos: prod.ok ? prod.data.productos || [] : [],
      };
    } finally {
      setCargando(false);
    }
  };

  const cargaRef = useRef(null);
  useEffect(() => {
    cargaRef.current = cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usaMultiempresa = !!config?.multiempresa;
  const nombreEmpresa = (emp) => {
    const e = (config?.empresas || []).find((x) => String(x.empresa) === String(emp));
    return `${emp} — ${e?.nombre || `Empresa ${emp}`}`;
  };
  const monedaLabel = etiquetaMoneda(moneda);
  // Moneda en la que están expresados producto.precio/mprecio y el límite
  // de crédito del cliente. Sin catálogo de monedas el backend responde
  // "DOP" y todo se comporta como siempre.
  const monedaBase = config?.base || "DOP";
  const labelBase = etiquetaMoneda(monedaBase);
  const tasaus = toNum(config?.tasaus);

  const disponibles = useMemo(
    () => productos.filter((p) => !items.some((i) => i.chasis === p.chasis)),
    [productos, items]
  );

  const total = useMemo(() => items.reduce((acc, i) => acc + toNum(i.precio), 0), [items]);

  // Un grupo por empresa de los chasis (un conduce por grupo al guardar).
  const grupos = useMemo(() => {
    if (!usaMultiempresa) return [{ empresa: null, items, total }];
    const mapa = new Map();
    items.forEach((i) => {
      const emp = Number(i.empresa) || 1;
      if (!mapa.has(emp)) mapa.set(emp, []);
      mapa.get(emp).push(i);
    });
    return [...mapa.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([emp, its]) => ({
        empresa: emp,
        items: its,
        total: its.reduce((acc, i) => acc + toNum(i.precio), 0),
      }));
  }, [usaMultiempresa, items, total]);

  // Mismo criterio que la factura: el precio pactado no puede quedar bajo el
  // mínimo del chasis (producto.mprecio, en la moneda BASE), convertido si el
  // conduce va en otra. Al facturar se revalida contra el mínimo de ese momento.
  const minimoEnMoneda = (i) => precioEnMoneda(i.mprecio, moneda, tasa, monedaBase);
  const bajoElMinimo = (i) => minimoEnMoneda(i) > 0 && toNum(i.precio) < minimoEnMoneda(i);
  const hayBajoMinimo = useMemo(() => items.some(bajoElMinimo), [items, moneda, tasa, monedaBase]);

  const limpiar = () => {
    setCliente(null);
    setMoneda("P");
    setTasa(String(config?.tasaus ?? ""));
    setConcepto("");
    setFecha("");
    setItems([]);
    setProdSel(null);
  };

  const agregarChasis = async (prod) => {
    setProdSel(null);
    if (!prod) return;
    if (items.some((i) => i.chasis === prod.chasis)) {
      showAlert({ message: "El chasis ya está agregado al conduce", color: "warning" });
      return;
    }
    setValidando(true);
    try {
      const { ok, data } = await post("/api/xmayor_validarchasis", { chasis: prod.chasis });
      if (!ok) {
        showAlert({ message: data.Error || "El chasis no está disponible", color: "error" });
        return;
      }
      const precio = precioEnMoneda(prod.precio, moneda, tasa, monedaBase);
      setItems((prev) => [
        ...prev,
        {
          chasis: prod.chasis,
          marca: prod.marca,
          modelo: prod.modelo,
          colore: prod.colore,
          ano: prod.ano,
          maquina: prod.maquina,
          // empresa dueña del chasis: decide en qué conduce sale
          empresa: Number(prod.empresa ?? data.producto?.empresa) || 1,
          precio: precio.toFixed(2),
          mprecio: toNum(prod.mprecio),
        },
      ]);
    } finally {
      setValidando(false);
    }
  };

  const actualizarPrecio = (chasis, v) => {
    setItems((prev) => prev.map((i) => (i.chasis === chasis ? { ...i, precio: v } : i)));
  };

  const quitarChasis = (chasis) => {
    setItems((prev) => prev.filter((i) => i.chasis !== chasis));
  };

  // ---- Borrador ("stand by", ver lib/borradorventa) ----
  // Los chasis no quedan reservados mientras la entrega está en pausa: al
  // retomar se revalidan uno por uno y los que ya no están disponibles se
  // quitan (los precios pactados de los demás se conservan).
  const [restaurando, setRestaurando] = useState(false);
  const restaurar = async (d) => {
    setRestaurando(true);
    try {
      const carga = (await cargaRef.current) || {};
      const conf = carga.conf || {};
      const cli = d.cliente
        ? (carga.clientes || []).find((c) => String(c.id) === String(d.cliente.id)) || d.cliente
        : null;
      setCliente(cli);
      setMoneda(d.moneda || "P");
      setTasa(d.tasa ?? String(conf.tasaus ?? ""));
      setConcepto(d.concepto || "");
      setFecha(d.fecha || "");
      setProdSel(null);

      const guardados = d.items || [];
      const validaciones = await Promise.all(
        guardados.map((i) => post("/api/xmayor_validarchasis", { chasis: i.chasis }))
      );
      const fuera = guardados.filter((_, k) => !validaciones[k].ok);
      // La empresa se toma de la validación: los borradores viejos no la traen.
      setItems(
        guardados
          .map((i, k) => ({
            ...i,
            empresa: Number(validaciones[k].data?.producto?.empresa ?? i.empresa) || 1,
            // el año no viajaba en los borradores viejos: sale del inventario
            ano: i.ano ?? (carga.productos || []).find((p) => p.chasis === i.chasis)?.ano,
          }))
          .filter((_, k) => validaciones[k].ok)
      );
      if (fuera.length) {
        showAlert({
          message: `${fuera.length === 1 ? "El chasis" : "Los chasis"} ${fuera
            .map((i) => i.chasis)
            .join(", ")} ya no ${
            fuera.length === 1 ? "está disponible y se quitó" : "están disponibles y se quitaron"
          } de la entrega.`,
          color: "warning",
        });
      }
    } finally {
      setRestaurando(false);
    }
  };

  const borrador = useBorradorVenta({
    tipo: "conduce",
    datos: {
      cliente: cliente
        ? {
            id: cliente.id,
            descrip: cliente.descrip,
            rnc: cliente.rnc,
            limcredito: cliente.limcredito,
          }
        : null,
      moneda,
      tasa,
      concepto,
      fecha,
      items,
    },
    resumen: {
      cliente: cliente?.descrip || "",
      chasis: items.length === 1 ? items[0].chasis : "",
      detalle: items.length > 1 ? `${items.length} chasis` : "",
    },
    conDatos: !!cliente || items.length > 0 || !!concepto.trim(),
    restaurar,
    autoRestaurar: restaurarBorrador,
    pausado: cargando || restaurando,
  });

  const guardar = async () => {
    if (!cliente) {
      showAlert({ message: "Debe seleccionar el cliente", color: "warning" });
      return;
    }
    if (items.length === 0) {
      showAlert({ message: "Debe agregar al menos un chasis al conduce", color: "warning" });
      return;
    }
    const t = toNum(tasa);
    if (t <= 0) {
      showAlert({ message: "La tasa no puede estar en 0", color: "warning" });
      return;
    }
    if (tasaus > 0 && t < tasaus) {
      showAlert({
        message: `La tasa no puede ser menor que la mínima (${fmtMoney(tasaus)})`,
        color: "warning",
      });
      return;
    }
    if (!concepto.trim()) {
      showAlert({ message: "Debe indicar el concepto del conduce", color: "warning" });
      return;
    }
    if (fecha && fecha > hoyLocalISO()) {
      showAlert({ message: "La fecha del conduce no puede ser futura", color: "warning" });
      return;
    }
    const bajoMinimo = items.find(bajoElMinimo);
    if (bajoMinimo) {
      showAlert({
        message: `El precio del chasis ${
          bajoMinimo.chasis
        } no puede ser menor al precio mínimo (${monedaLabel} ${fmtMoney(
          minimoEnMoneda(bajoMinimo)
        )})`,
        color: "warning",
      });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_guardar", {
        cliente: cliente.id,
        moneda,
        tasa: t,
        concepto: concepto.trim(),
        fecha,
        items: items.map((i) => ({ chasis: i.chasis, precio: toNum(i.precio) })),
      });
      if (ok) {
        // Un conduce por empresa; cada uno lleva sus chasis para imprimirlo.
        const fechaImpresa = (
          fecha ? new Date(`${fecha}T00:00:00`) : new Date()
        ).toLocaleDateString("es-DO");
        const conduces = (data.conduces || [data]).map((c) => {
          const lineas = c.chasis ? items.filter((i) => c.chasis.includes(i.chasis)) : items;
          return {
            empresa: c.empresa,
            compania: c.compania || config?.compania,
            conduce: {
              id: c.id,
              fecha: fechaImpresa,
              clientenombre: cliente.descrip,
              rnc: cliente.rnc,
              concepto,
              monedaTexto: monedaLabel,
              valorpiso: c.total ?? total,
              motores: lineas.length,
              creado: usuarioLog,
            },
            lineas: lineas.map((i) => ({ ...i, estado: "" })),
          };
        });
        if (conduces.length === 1) {
          showAlert({ message: `Conduce #${conduces[0].conduce.id} registrado`, color: "success" });
          imprimirConduce(conduces[0]);
        } else {
          showAlert({
            message: `${conduces.length} conduces registrados (${conduces
              .map((c) => `#${c.conduce.id}`)
              .join(", ")})`,
            color: "success",
          });
          setEmitidos(conduces);
        }
        limpiar();
        if (onSaved) onSaved();
        cargarProductos();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar el conduce", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columns = [
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 160 },
    { field: "marca", headerName: "Marca", width: 120 },
    { field: "modelo", headerName: "Modelo", width: 120 },
    { field: "colore", headerName: "Color", width: 110 },
    {
      field: "precio",
      headerName: "Precio pactado",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.precio}
          onChange={(e) => actualizarPrecio(params.row.chasis, e.target.value)}
          error={bajoElMinimo(params.row)}
          title={
            minimoEnMoneda(params.row) > 0
              ? `Precio mínimo ${fmtMoney(minimoEnMoneda(params.row))}`
              : ""
          }
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "mprecio",
      headerName: "Mínimo",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        minimoEnMoneda(params.row) > 0 ? fmtMoney(minimoEnMoneda(params.row)) : "—",
    },
    {
      field: "acciones",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => quitarChasis(params.row.chasis)} title="Quitar">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registrar Conduce x Mayor"
        subtitle="Entrega motores en consignación: no genera factura ni deuda hasta que el cliente venda"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <AvisoBorradorVenta
          borrador={borrador.oferta}
          etiqueta="entrega en consignación"
          onContinuar={borrador.continuar}
          onDescartar={borrador.descartar}
          sx={{ mb: 2.4 }}
        />
        {restaurando && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
              Retomando la entrega en pausa y validando los chasis...
            </Typography>
          </Stack>
        )}
        <Box sx={sectionBox}>
          <SectionTitle icon={LocalShippingOutlinedIcon} color={FP.color.accentBlue}>
            Datos del Conduce
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarClientes}
                options={clientes}
                loading={cargando}
                value={cliente}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.id} — ${o.descrip || ""}`)}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => setCliente(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.id} — {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.rnc ? `RNC ${o.rnc}` : ""].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Cliente" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Moneda"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                disabled={items.length > 0}
                helperText={items.length > 0 ? "Bloqueada con chasis agregados" : undefined}
                sx={fpInputSx}
              >
                <MenuItem value="P">{etiquetaMoneda("P")}</MenuItem>
                <MenuItem value="U">USD</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                placeholder="0.00"
                helperText={`Mínima: ${fmtMoney(tasaus)}`}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center" }}>
              {cliente && toNum(cliente.limcredito) > 0 && (
                <Chip
                  label={`Límite: ${labelBase} ${fmtMoney(cliente.limcredito)}`}
                  sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: hoyLocalISO() }}
                helperText="En blanco: hoy"
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <TextField
                fullWidth
                label="Concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={sectionBox}>
          <SectionTitle
            icon={DirectionsCarFilledOutlinedIcon}
            color={FP.color.success}
            extra={
              <Chip
                label={`Valor entregado: ${monedaLabel} ${fmtMoney(total)}`}
                sx={{
                  ml: "auto",
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            }
          >
            Chasis a entregar
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarProductos}
                options={disponibles}
                loading={cargando || validando}
                value={prodSel}
                getOptionLabel={(o) =>
                  typeof o === "string"
                    ? o
                    : `${o.chasis} — ${[o.marca, o.modelo].filter(Boolean).join(" ")}`
                }
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                onChange={(_, v) => agregarChasis(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.chasis}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.chasis}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[
                          [o.marca, o.modelo, o.colore].filter(Boolean).join(" "),
                          `${monedaLabel} ${fmtMoney(
                            precioEnMoneda(o.precio, moneda, tasa, monedaBase)
                          )}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agregar chasis"
                    helperText="Busque por chasis, marca o modelo"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {validando && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={18} />
              </Grid>
            )}
          </Grid>
          {grupos.length > 1 && (
            <Typography sx={{ mt: 1.6, fontSize: "0.82rem", color: FP.color.textSecondary }}>
              Los chasis son de {grupos.length} empresas: se creará un conduce por cada una.
            </Typography>
          )}
          {(items.length === 0 ? [{ empresa: null, items: [], total: 0 }] : grupos).map((g) => (
            <Box key={g.empresa ?? "todos"} sx={{ mt: 2 }}>
              {usaMultiempresa && g.empresa != null && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.8, flexWrap: "wrap", rowGap: 0.6 }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: FP.color.navy }}>
                    Empresa {nombreEmpresa(g.empresa)}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${g.items.length} chasis · ${monedaLabel} ${fmtMoney(g.total)}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              )}
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={g.items}
                  columns={columns}
                  getRowId={(r) => r.chasis}
                  rowHeight={52}
                  hideFooter
                  slots={{ noRowsOverlay: NoRowsOverlay }}
                  sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
                  disableRowSelectionOnClick
                />
              </Box>
            </Box>
          ))}
          {hayBajoMinimo && (
            <Typography
              sx={{ mt: 1.4, fontWeight: 700, fontSize: "0.82rem", color: FP.color.danger }}
            >
              Hay precios por debajo del mínimo del chasis
            </Typography>
          )}
        </Box>

        <Box sx={{ ...sectionBox, mb: 0, display: "flex", justifyContent: "flex-end" }}>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando || cargando || restaurando || hayBajoMinimo}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar e imprimir conduce
          </Button>
        </Box>
      </Box>

      {/* Varias empresas: un conduce por empresa, se imprimen uno a uno */}
      <Dialog open={!!emitidos} onClose={() => setEmitidos(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Conduces registrados</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 1.6 }}>
            Se creó un conduce por cada empresa. Imprima cada uno.
          </Typography>
          <Stack spacing={1.2}>
            {(emitidos || []).map((c) => (
              <Stack
                key={c.conduce.id}
                direction="row"
                alignItems="center"
                spacing={1.4}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 1.4 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                    Conduce #{c.conduce.id}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                    {c.empresa != null ? `${nombreEmpresa(c.empresa)} · ` : ""}
                    {c.conduce.motores} chasis · {c.conduce.monedaTexto}{" "}
                    {fmtMoney(c.conduce.valorpiso)}
                  </Typography>
                </Box>
                <Button
                  startIcon={<PrintOutlinedIcon />}
                  onClick={() => imprimirConduce(c)}
                  sx={btnOutline(FP.color.navy)}
                >
                  Imprimir
                </Button>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmitidos(null)} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
